import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import UserProfileBox from "../userProfileBox/UserProfileBox"; // Adjust the import path
import API_URL from "../../apiConfig";
import Loader from "../../components/common/Loader/Loader";
import { TaskService } from "../../services/task/taskService";
import { TeamMembersService } from "../../services/teamMembers/teamMembersService";
import { HelenService } from "../../services/helen/helenService";
import { UserService } from "../../services/user/userService";
import TopBar from "../../components/TopBar/TopBar";
import { getUserId } from "../../utils/tokenUtils";
interface ProfilePopupProps {
  onClose: () => void;
}

const videos = [
  {
    title: "Add Brand and Category communication guideline",
    duration: "35 sec",
    url: "https://vimeo.com/1094718420/32ed3912b7",
  },
  {
    title: "Provide Product details",
    duration: "44 sec",
    url: "https://vimeo.com/1094718499/c48b73e3ee",
  },
  {
    title: "Control the task queue",
    duration: "18 sec",
    url: "https://vimeo.com/1094718579/3918726c97",
  },
  {
    title: "Access and download task history",
    duration: "34 sec",
    url: "https://vimeo.com/1094718638/e9bb3a6327",
  },
];

const getVimeoEmbedUrl = (url: string) => {
  const match = url.match(/vimeo\.com\/(\d+)\/([a-zA-Z0-9]+)/);
  return match
    ? `https://player.vimeo.com/video/${match[1]}?h=${match[2]}`
    : "";
};
const toHighResThumbnail = (url: string) => {
  return url.replace(/_\d+x\d+\.jpg$/, "_1280.jpg");
};

const Dashboard = () => {
  const [firstName, setFirstName] = useState("");
  const [userId, setUserId] = useState(getUserId());
  const navigate = useNavigate();
  const [completedCount, setCompletedCount] = useState(0);
  const [queueCount, setQueueCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [activeVideo, setActiveVideo] = useState<{
    title: string;
    embedUrl: string;
  } | null>(null);
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    const fetchThumbnails = async () => {
      const results = await Promise.all(
        videos.map(async (video) => {
          const response = await fetch(
            `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
              video.url
            )}`
          );
          const data = await response.json();
          return toHighResThumbnail(data.thumbnail_url);
        })
      );
      setThumbnails(results);
    };

    fetchThumbnails();
  }, []);

  // Fetch user data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await UserService.getUser();
        setUserData(response.data.user || null);
        setFirstName(response.data.user?.firstName || "");
      } catch (err: any) {
        setError(err.message || "Unknown error");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const [isHidingTutorial, setIsHidingTutorial] = useState(false);

  const handleUnderstood = async () => {
    try {
      setIsHidingTutorial(true); // Trigger CSS animation

      // Wait for animation (300ms) before hiding from DOM
      setTimeout(async () => {
        await UserService.updateUserTutorialFlag(userId, true);
        setUserData((prev: any) => ({
          ...prev,
          isTutorialCompleted: true,
        }));
      }, 300);
    } catch (error) {
    }
  };

  if (isLoading)
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loader inline={true} />
      </div>
    );

  if (error) return <div>Error loading bots: {error}</div>;

  return (
    <div className="dashboard-container">
      <TopBar />
      {/* Header */}
      <div className="header-text">
        <span className="welcome-highlight">Welcome {firstName}!</span> Let’s
        have a productive day…
      </div>

      {/* Video Carousel */}
      {!userData?.isTutorialCompleted && (
        <div
          className={`card tutorial-section ${
            isHidingTutorial ? "fade-out" : "fade-in"
          }`}
        >
          <div className="section-header">
            <div className="section-title">
              Discover how Helen works—watch, learn, and generate content
              instantly
            </div>
            <button className="understood-button" onClick={handleUnderstood}>
              Understood
            </button>
          </div>

          <div className="video-grid">
            {videos.map((video, index) => (
              <div className="video-preview" key={index}>
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    backgroundColor: "#f0f0f0",

                    overflow: "hidden",
                  }}
                >
                  {/* Thumbnail or Skeleton */}
                  {!thumbnails[index] ? (
                    <div
                      style={{
                        width: "100%",
                        height: "150px",
                        backgroundColor: "#ddd",
                        animation: "pulse 1.5s infinite",
                      }}
                    />
                  ) : (
                    <img
                      src={thumbnails[index]}
                      alt={video.title}
                      onClick={() => {
                        setActiveVideo({
                          title: video.title,
                          embedUrl: getVimeoEmbedUrl(video.url),
                        });
                      }}
                      style={{
                        width: "100%",
                        height: "150px",
                        objectFit: "cover",
                      }}
                    />
                  )}

                  {/* Play Button */}
                  <button
                    onClick={() =>
                      setActiveVideo({
                        title: video.title,
                        embedUrl: getVimeoEmbedUrl(video.url),
                      })
                    }
                    className="video-play-button"
                  >
                    ▶
                  </button>
                </div>

                {/* Text or Skeleton */}
                {!thumbnails[index] ? (
                  <div style={{ padding: "12px" }}>
                    <div
                      style={{
                        width: "70%",
                        height: "16px",
                        backgroundColor: "#ddd",
                        borderRadius: "4px",
                        marginBottom: "6px",
                        animation: "pulse 1.5s infinite",
                      }}
                    />
                    <div
                      style={{
                        width: "30%",
                        height: "14px",
                        backgroundColor: "#ddd",
                        borderRadius: "4px",
                        animation: "pulse 1.5s infinite",
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ padding: "12px" }}>
                    <h4
                      style={{
                        margin: "0 0 4px 0",
                        fontSize: "15px",
                        fontWeight: 600,
                      }}
                    >
                      {video.title}
                    </h4>
                    <p style={{ margin: 0, fontSize: "13px", color: "#888" }}>
                      {video.duration}
                    </p>
                  </div>
                )}
              </div>
            ))}

            {/* ✅ Modal (Active Video Player) */}
            {activeVideo && (
              <div
                onClick={() => setActiveVideo(null)}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  zIndex: 9999,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(0,0,0,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: "#000",
                    borderRadius: "12px",
                    padding: "16px",
                    width: "90%",
                    maxWidth: "720px",
                    position: "relative",
                  }}
                >
                  <button
                    onClick={() => setActiveVideo(null)}
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "12px",
                      fontSize: "24px",
                      background: "transparent",
                      color: "#fff",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    ✕
                  </button>
                  <iframe
                    src={activeVideo.embedUrl}
                    width="100%"
                    height="400"
                    frameBorder="0"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    title={activeVideo.title}
                    style={{ borderRadius: "8px" }}
                  ></iframe>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* 🧠 Brand Voice Section — Only for Admins */}
      {userData?.role?.toLowerCase() === "admin" && (
        <div className="brand-voice-section">
          <div className="brand-voice-texts">
            <div className="brand-voice-title">
              Get Started by defining your brand voice
            </div>
            <div className="brand-voice-description">
              The brand voice is information, using vivid descriptions,
              technical details, comparisons, short paragraphs, bullet points,
              conversational tone and brand values that highlight product
              benefits.
            </div>
          </div>
          <button
            className="brand-voice-button"
            onClick={() => navigate("/admin/brand")}
          >
            Add Brand Guidelines
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="task-stats">
        {[
          {
            label: "Completed Tasks",
            value: completedCount,
            imgSrc: "/images/CompletedTasks.svg",
          },
          {
            label: "Tasks in Queue",
            value: queueCount,
            imgSrc: "/images/TasksInqueue.svg",
          },
          {
            label: "Daily Interactions",
            value: `${0}%`,
            imgSrc: "/images/CreditUsages.svg",
          },
        ].map((stat, index) => (
          <div key={index} className="stat-box">
            <div className="stat-card">
              <img src={stat.imgSrc} alt={stat.label} className="stat-icon" />
              <div className="stat-text">
                <p className="stat-label">{stat.label}</p>
                <span className="stat-value">{stat.value}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bots Section */}
      {/* <div className="helen-bots-section">
        <h2>Bots Assigned To You</h2>
        <div className="helen-bots-grid">
          {[...assignedHelenIds, ...remainingHelenIds]
            .sort((a, b) => {
              const numA = parseInt(a.id.split("_")[1]);
              const numB = parseInt(b.id.split("_")[1]);
              return numA - numB;
            })
            .map((helen) => {
              const { id: helenId, assignedAt } = helen;

              // ✅ Use correct key: helen_ids_assigned
              const assignedBot = bots.find((bot) =>
                bot.helen_ids_assigned?.some(
                  (h: { id: string; assignedAt?: string; assigned_at?: string }) =>
                    h.id === helenId
                )
              );

              const botStatus = "Active"; // make dynamic if needed

              return (
                <div className="helen-bot-card" key={helenId}>
                  <div className="helen-bot-header">
                    <div className="helen-bot-name">
                      {helenId.replace("_", " ").toUpperCase()}
                    </div>
                    <span className={`helen-bot-status ${botStatus.toLowerCase()}`}>
                      {botStatus}
                    </span>
                  </div>

                  <hr className="divider" />

                  {assignedBot ? (
                    <>
                      <div className="helen-assigned-info">
                        <div className="helen-info-block">
                          <p className="label">QUEUED TASKS</p>
                          <p className="value">
                            {pendingTasksPerHelen[helen.id] != null
                              ? pendingTasksPerHelen[helen.id]
                              : 0}
                          </p>
                        </div>
                        <div className="helen-info-block">
                          <p className="label">CREDITS USED</p>
                          <p className="value">
                            {helenEfficiencyMap[helen.id] != null
                              ? `${helenEfficiencyMap[helen.id]} / 5`
                              : "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="helen-manager">
                        <p className="label">ASSIGNED MANAGER :</p>
                        <div className="helen-manager-details">
                          <img className="helen-avatar" src="default.jpg" alt="avatar" />
                          <span>{assignedBot.full_name}</span>
                        </div>
                      </div>

                      <div className="helen-timer">
                        Working shift ends in --:-- mins EST
                      </div>
                      <button className="helen-view-button">View Queue</button>
                    </>
                  ) : (
                    <>
                      <div className="helen-unassigned-message row">
                        <div className="helen-warning-circle">
                          <img
                            src="/images/infocircles.svg"
                            alt="!"
                            className="helen-warning-icon"
                          />
                        </div>
                        <div className="helen-warning-texts">
                          <p className="title">Helen Not Assigned</p>
                          <p className="subtitle">
                            Assign a reporting manager to Helen so they can start
                            adding tasks!
                          </p>
                        </div>
                      </div>
                      <hr className="divider" />
                      <div className="helen-footer">
                        <div className="helen-timer">
                          Working shift ends in --:-- mins EST
                        </div>
                        <button
                          className="helen-assign-button"
                          onClick={() => navigate("/managebots")}
                        >
                          Assign Member
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
        </div>
      </div> */}

      {/* Recent Task Details Table */}
      <div className="card recent-tasks">
        <h3>Recent Task Details</h3>
        <table className="task-table">
          <thead>
            <tr>
              <th style={{ width: "5%" }}></th>
              <th style={{ width: "15%" }}>TASK ID</th>
              <th style={{ width: "10%" }}>NAME</th>
              <th style={{ width: "10%" }}>TYPE</th>
              <th style={{ width: "15%" }}>COUNT</th>
              <th style={{ width: "15%" }}>UPLOADED ON</th>
              <th style={{ width: "10%" }}>PROCESS TIME</th>
              <th style={{ width: "10%" }}>STATUS</th>
              <th style={{ width: "10%" }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={9} style={{ textAlign: "center" }}>
                No completed tasks found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;
